import React, { FC, useContext, useMemo } from 'react';

import {
  Button,
  CashbackIcon,
  CollapsedLeftLogoIcon,
  H2,
  Paragraph,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { SettingsFrame } from 'components';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { getOptimizedImage } from '../ShellBackground/utils';
import { GameControllerIcon, HomeIcon } from './icon';
import { IPreviewBlockProps } from './interfaces';
import s from './PreviewBlock.module.scss';
import { isDarkColorHSP } from './utils';

export const PreviewBlock: FC<IPreviewBlockProps> = ({ state, save }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        currency: { sign },
      },
    },
  } = useContext(SettingsContext);

  const isAvailable = useIsFeatureAvailable('shell_skin');

  const styleBlock = useMemo(() => {
    const { shellBgSkin, shellSkinBackgroundColor } = state;

    let block = undefined;

    if (shellBgSkin && shellSkinBackgroundColor) {
      block = {
        background: `linear-gradient(${shellSkinBackgroundColor}3D, ${shellSkinBackgroundColor}3D), url(${getOptimizedImage(
          shellBgSkin
        )}) center / cover no-repeat`,
      };
    } else if (shellBgSkin) {
      block = {
        background: `url(${getOptimizedImage(
          shellBgSkin
        )}) center / cover no-repeat`,
      };
    } else if (shellSkinBackgroundColor) {
      block = {
        backgroundColor: shellSkinBackgroundColor,
      };
    }

    return block;
  }, [state.shellSkinBackgroundColor, state.shellBgSkin]);

  const styleAdditionalColor = useMemo(
    () => `linear-gradient(0deg, ${state.shellSkinAdditionalColor}3D, ${state.shellSkinAdditionalColor}3D),
linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))`,
    [state.shellSkinAdditionalColor]
  );

  return (
    <SettingsFrame>
      <div className={s.container}>
        <div className={s.header}>
          <H2>{t('settings.shell.customization.preview.title')}</H2>
          <Paragraph color="secondary" size="s" className={s.text}>
            {t('settings.shell.customization.preview.description')}
          </Paragraph>
        </div>
        <div className={s.body}>
          <div className={s.wrapper} style={{ ...styleBlock }}>
            <div
              className={s.menu}
              style={{ background: styleAdditionalColor }}
            >
              <div className={s.logo}>
                {state.shellSkinLogo ? (
                  <img src={state.shellSkinLogo} alt="logo" />
                ) : (
                  <CollapsedLeftLogoIcon color="light" />
                )}
              </div>
              <div className={s.menu__buttons}>
                <div className={s.menu__button}>
                  <HomeIcon />
                </div>
                <div className={cn(s.menu__button, s.menu__button_active)}>
                  <div
                    className={s.active}
                    style={{ backgroundColor: state.shellSkinAccentColor }}
                  />
                  <GameControllerIcon color={state.shellSkinAccentColor} />
                </div>
              </div>
            </div>

            <div className={s.content}>
              <div
                className={cn(
                  s.content__header,
                  !isDarkColorHSP(state.shellSkinBackgroundColor) &&
                    !state.shellBgSkin &&
                    s.content__header_light
                )}
              >
                <Paragraph bold>{t('applications.games')}</Paragraph>
                <Paragraph bold>{t('nav.applications')}</Paragraph>
              </div>
              <div
                className={s.block}
                style={{
                  background: styleAdditionalColor,
                }}
              >
                <div className={s.block__header}>
                  <Paragraph className={s.block__title} color="white">
                    {t('client.deposit')}
                  </Paragraph>
                  <Paragraph
                    className={s.block__link}
                    style={{ color: state.shellSkinAccentColor }}
                    color="white"
                  >
                    {t('history')}
                  </Paragraph>
                </div>

                <div className={s.block__content}>
                  <Paragraph
                    size="l"
                    color="white"
                  >{`49 000 000 ${sign}`}</Paragraph>
                  <div
                    style={{ backgroundColor: state.shellSkinAccentColor }}
                    className={s.block__button}
                  >
                    <CashbackIcon
                      size="m"
                      className={cn(
                        isDarkColorHSP(state.shellSkinAccentColor)
                          ? s.color_white
                          : s.color_black
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={s.footer}>
          <Button onClick={save} disabled={!isAvailable}>
            {t('save')}
          </Button>
        </div>
      </div>
    </SettingsFrame>
  );
};
